import { useEffect, type ReactNode } from 'react';

/**
 * The title row of Create and Edit order (MYP-131): the title, the stepper and the page's links
 * (How it works, Cancel) on one line on a laptop. On a narrower screen the stepper drops to its own
 * line under the title, so neither is squeezed.
 */
export function OrderFlowHeader({
  title,
  stepper,
  actions,
}: {
  title: ReactNode;
  stepper: ReactNode;
  actions: ReactNode;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-1">
      <h1 className="min-w-0 text-2xl font-semibold text-ink">{title}</h1>
      <div className="order-3 w-full lg:order-none lg:flex lg:w-auto lg:flex-1 lg:justify-center">
        {stepper}
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-4">{actions}</div>
    </div>
  );
}

/**
 * The bar pinned to the bottom of the screen on Create and Edit order (MYP-131): what is selected,
 * and the buttons that move the order on. Continue is always in reach, however long the list
 * above, and the summary no longer needs a line of its own under the title.
 *
 * Sticky rather than fixed, so it sits inside the page's own column and never covers the sidebar
 * or the end of the page: at the very bottom it simply rests in place.
 */
export function OrderFlowBar({ children }: { children: ReactNode }) {
  /*
    While the bar is on screen, the browser keeps 6rem clear at the bottom whenever it scrolls a
    control into view — tabbing to the last row, the pager or "Show more" — so a focused control is
    never hidden behind the bar (WCAG 2.4.11). Put back as it was when the page is left.
  */
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.scrollPaddingBottom;
    root.style.scrollPaddingBottom = '6rem';
    return () => {
      root.style.scrollPaddingBottom = previous;
    };
  }, []);

  return (
    <div className="sticky bottom-0 z-30 -mx-4 mt-6 -mb-6 border-t border-line bg-surface/95 px-4 py-3 shadow-[0_-2px_8px_rgb(0_0_0/0.05)] backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:-mb-8 lg:px-8">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">{children}</div>
    </div>
  );
}
